import React, { useState, useEffect, useRef } from 'react';
import './PasscodePrompt.css';

function PasscodePrompt({ username, mode = 'verify', onSuccess, onCancel }) {
  const [passcode, setPasscode] = useState(['', '', '', '']);
  const [confirmPasscode, setConfirmPasscode] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const confirmInputRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (index, value, isConfirm = false) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const refs = isConfirm ? confirmInputRefs : inputRefs;
    const setter = isConfirm ? setConfirmPasscode : setPasscode;
    const current = isConfirm ? confirmPasscode : passcode;

    const newPasscode = [...current];
    newPasscode[index] = value;
    setter(newPasscode);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      refs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits are entered (verify mode only)
    if (!isConfirm && mode === 'verify' && index === 3 && value) {
      const fullPasscode = newPasscode.join('');
      if (fullPasscode.length === 4) {
        handleSubmit(fullPasscode);
      }
    }

    // In setup mode, move to confirm after first 4 digits
    if (!isConfirm && mode === 'setup' && index === 3 && value) {
      setTimeout(() => {
        confirmInputRefs[0].current?.focus();
      }, 100);
    }
  };

  const handleKeyDown = (e, index, isConfirm = false) => {
    const refs = isConfirm ? confirmInputRefs : inputRefs;
    const setter = isConfirm ? setConfirmPasscode : setPasscode;
    const current = isConfirm ? confirmPasscode : passcode;

    if (e.key === 'Backspace') {
      e.preventDefault();
      const newPasscode = [...current];

      if (current[index]) {
        // Clear current digit
        newPasscode[index] = '';
        setter(newPasscode);
      } else if (index > 0) {
        // Move to previous and clear it
        newPasscode[index - 1] = '';
        setter(newPasscode);
        refs[index - 1].current?.focus();
      }
    }
  };

  const handleSubmit = (codeToSubmit) => {
    const code = codeToSubmit || passcode.join('');

    if (code.length !== 4) {
      setError('Please enter a 4-digit passcode');
      return;
    }

    if (mode === 'setup') {
      const confirm = confirmPasscode.join('');
      if (confirm.length !== 4) {
        setError('Please confirm your passcode');
        return;
      }
      if (code !== confirm) {
        setError('Passcodes do not match');
        setPasscode(['', '', '', '']);
        setConfirmPasscode(['', '', '', '']);
        inputRefs[0].current?.focus();
        return;
      }
    }

    onSuccess(code);
  };

  const handlePaste = (e, isConfirm = false) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only allow 4 digits
    if (!/^\d{4}$/.test(pastedData)) return;

    const setter = isConfirm ? setConfirmPasscode : setPasscode;
    const digits = pastedData.split('');
    setter(digits);

    // Focus last input
    const refs = isConfirm ? confirmInputRefs : inputRefs;
    refs[3].current?.focus();

    // Auto-submit in verify mode
    if (!isConfirm && mode === 'verify') {
      setTimeout(() => handleSubmit(pastedData), 100);
    }
  };

  return (
    <div className="passcode-overlay">
      <div className="passcode-prompt">
        <div className="passcode-header">
          <h2 className="passcode-title">
            {mode === 'setup' ? '🔐 Set Passcode' : '🔐 Enter Passcode'}
          </h2>
          <p className="passcode-subtitle">
            {mode === 'setup'
              ? `Set a 4-digit passcode for ${username}`
              : `Enter passcode for ${username}`}
          </p>
        </div>

        <div className="passcode-body">
          <div className="passcode-input-group">
            <div className="passcode-inputs">
              {passcode.map((digit, index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value, false)}
                  onKeyDown={(e) => handleKeyDown(e, index, false)}
                  onPaste={(e) => handlePaste(e, false)}
                  className="passcode-digit"
                  autoComplete="off"
                />
              ))}
            </div>
          </div>

          {mode === 'setup' && (
            <>
              <div className="passcode-separator">Confirm Passcode</div>
              <div className="passcode-input-group">
                <div className="passcode-inputs">
                  {confirmPasscode.map((digit, index) => (
                    <input
                      key={index}
                      ref={confirmInputRefs[index]}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value, true)}
                      onKeyDown={(e) => handleKeyDown(e, index, true)}
                      onPaste={(e) => handlePaste(e, true)}
                      className="passcode-digit"
                      autoComplete="off"
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {error && <div className="passcode-error">{error}</div>}
        </div>

        <div className="passcode-actions">
          {mode === 'setup' && (
            <button
              className="passcode-submit-btn"
              onClick={() => handleSubmit()}
            >
              Set Passcode
            </button>
          )}
          <button
            className="passcode-cancel-btn"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default PasscodePrompt;
